import { GameZoneSheet } from '@/components/molecules/GameZoneModal/GameZoneSheet'
import React, { useMemo, useState } from 'react'
import { gql, useQuery } from '@apollo/client'
import {
  useBreakpointValue,
  Box,
  Button,
  Divider,
  HStack,
  Modal,
  ModalBody,
  CloseButton,
  Flex,
  ModalContent,
  ModalOverlay,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react'

import { useCashier } from '@/context/CashierProvider'
import { useBalanceStore } from '@/context/UserBalance'
import { getTokenByDenom } from '@/services/token-service'
import { GameZoneNotice } from '@/components/molecules/GameZoneNotice/GameZoneNotice'
import { useTranslation } from 'next-i18next/pages'

type GameZoneWithdrawModalProps = {
  embedded?: boolean
  onClose: () => void
  onSuccess?: () => void
}

type TokenPricesQuery = {
  tokenPrices?: {
    nodes: Array<{ symbol: string; value: string } | null>
  } | null
}

const TOKEN_PRICES_QUERY = gql`
  query GameZoneWithdrawTokenPrices {
    tokenPrices {
      nodes {
        symbol
        value
      }
    }
  }
`

const formatAmount = (value: number, maximumFractionDigits: number) =>
  value.toLocaleString('en-US', { maximumFractionDigits })

const formatUsd = (value: number) =>
  value.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })

export const GameZoneWithdrawModal: React.FC<GameZoneWithdrawModalProps> = ({
  onClose,
  onSuccess,
  embedded = false,
}) => {
  const isMobile = useBreakpointValue({ base: true, md: false }) ?? false
  const { t } = useTranslation('common')
  const balances = useBalanceStore((state) => state.balance)
  const { withdraw, isProcessing } = useCashier()
  const [isWithdrawingAll, setIsWithdrawingAll] = useState(false)
  const { data } = useQuery<TokenPricesQuery>(TOKEN_PRICES_QUERY)

  const gameBalances = useMemo(
    () =>
      Object.entries(balances)
        .filter(([, balance]) => balance.game_balance.chainValue.gt(0))
        .map(([denom, balance]) => {
          const token = getTokenByDenom(denom)
          const symbol = token?.currency.toLowerCase()
          const price = Number(
            data?.tokenPrices?.nodes.find((tokenPrice) => tokenPrice?.symbol.toLowerCase() === symbol)?.value ??
              (symbol === 'usdc' || symbol === 'usdt' ? 1 : 0)
          )

          return {
            denom,
            amount: balance.game_balance,
            token,
            usdValue: balance.game_balance.humanNumber * price,
          }
        }),
    [balances, data]
  )

  const totalWithdraw = useMemo(
    () => gameBalances.reduce((total, balance) => total + balance.usdValue, 0),
    [gameBalances]
  )

  const handleWithdrawAll = async () => {
    setIsWithdrawingAll(true)

    for (const balance of gameBalances) {
      const success = await withdraw(balance.amount, balance.denom)
      if (!success) {
        setIsWithdrawingAll(false)
        return
      }
    }

    setIsWithdrawingAll(false)
    onClose()
    onSuccess?.()
  }

  const isBusy = isProcessing || isWithdrawingAll

  const content = (
    <VStack align="stretch" spacing={{ base: 4, sm: 5 }}>
      <HStack justify="space-between" align="center" minH="36px" spacing={4}>
        <Text fontSize={{ base: '18px', sm: '20px' }} fontWeight={700} lineHeight={1.2} letterSpacing="-0.4px">
          {t('game-zone.withdraw-from')}
        </Text>
        <CloseButton
          position="static"
          size="sm"
          w={{ base: '44px', md: '24px' }}
          h={{ base: '44px', md: '24px' }}
          flexShrink={0}
          color="ninja.200"
          bg="whiteAlpha.100"
          borderRadius="full"
          isDisabled={isBusy}
          onClick={onClose}
          _hover={{ bg: 'whiteAlpha.200', color: 'white' }}
        />
      </HStack>

      <VStack
        align="stretch"
        spacing={0}
        px={{ base: 3, sm: 4 }}
        bg="ninja.700"
        border="1px solid"
        borderColor="whiteAlpha.100"
        borderRadius="12px"
        overflow="hidden"
      >
        {gameBalances.map(({ denom, amount, token, usdValue }, index) => (
          <React.Fragment key={denom}>
            <HStack justify="space-between" minH="52px" py={3} spacing={3}>
              <HStack spacing={3} minW={0}>
                <Box boxSize="24px" flexShrink={0}>
                  {token?.icon('24px')}
                </Box>
                <Text fontSize="14px" fontWeight={700} lineHeight={1.2} noOfLines={1}>
                  {formatAmount(amount.humanNumber, token?.precisions ?? 6)} {token?.name ?? denom.toUpperCase()}
                </Text>
              </HStack>
              <Text color="ninja.200" fontSize="14px" fontWeight={500} lineHeight={1.2} whiteSpace="nowrap">
                ≈${formatUsd(usdValue)}
              </Text>
            </HStack>
            {index < gameBalances.length - 1 && <Divider borderColor="whiteAlpha.100" opacity={1} />}
          </React.Fragment>
        ))}

        <Divider borderColor="whiteAlpha.200" opacity={1} />
        <HStack justify="space-between" minH="52px" py={3}>
          <Text color="ninja.200" fontSize="14px" fontWeight={500} lineHeight={1.2}>
            {t('game-zone.total-withdraw')}
          </Text>
          <Text fontSize="16px" fontWeight={700} lineHeight={1.2} letterSpacing="-0.32px" whiteSpace="nowrap">
            ≈${formatUsd(totalWithdraw)}
          </Text>
        </HStack>
      </VStack>

      <GameZoneNotice>{t('game-zone.withdraw-notice')}</GameZoneNotice>

      <Stack direction={{ base: 'column', md: 'row' }} spacing={3} align="stretch">
        <Button
          h={{ base: 'auto', md: '52px' }}
          minH={{ base: '56px', md: '52px' }}
          w={{ base: 'full', md: 'auto' }}
          flex={{ base: '0 0 auto', md: 1 }}
          py={3}
          fontSize="16px"
          whiteSpace="normal"
          borderRadius="12px"
          size="lg"
          variant="secondary"
          isDisabled={isBusy}
          onClick={onClose}
        >
          {t('game-zone.cancel')}
        </Button>
        <Button
          h={{ base: 'auto', md: '52px' }}
          minH={{ base: '56px', md: '52px' }}
          w={{ base: 'full', md: 'auto' }}
          flex={{ base: '0 0 auto', md: 1 }}
          py={3}
          fontSize="16px"
          whiteSpace="normal"
          borderRadius="12px"
          size="lg"
          isLoading={isBusy}
          isDisabled={gameBalances.length === 0}
          onClick={handleWithdrawAll}
        >
          {t('game-zone.withdraw-all')}
        </Button>
      </Stack>
    </VStack>
  )

  if (isMobile) {
    return (
      <GameZoneSheet onClose={onClose} isBusy={isBusy}>
        {content}
      </GameZoneSheet>
    )
  }

  if (embedded) {
    return (
      <Flex
        position="absolute"
        inset={0}
        zIndex={30}
        justify="center"
        align="flex-start"
        overflowY="auto"
        overscrollBehavior="contain"
        p={{ base: 3, md: 6 }}
        bg="rgba(4, 3, 24, 0.82)"
        backdropFilter="blur(6px)"
      >
        <Box
          w="full"
          maxW="560px"
          my="auto"
          flexShrink={0}
          p={{ base: 5, sm: 7 }}
          bg="ninja.600"
          color="white"
          borderRadius="20px"
          border="1px solid"
          borderColor="whiteAlpha.100"
          boxShadow="0 24px 64px rgba(0, 0, 0, 0.38)"
        >
          {content}
        </Box>
      </Flex>
    )
  }

  return (
    <Modal isOpen isCentered onClose={onClose} closeOnOverlayClick={!isBusy}>
      <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(4px)" />
      <ModalContent
        w="calc(100% - 32px)"
        maxW="560px"
        m={4}
        p={{ base: 5, sm: 7 }}
        bg="ninja.600"
        color="white"
        borderRadius="20px"
        border="1px solid"
        borderColor="whiteAlpha.100"
        boxShadow="0 24px 64px rgba(0, 0, 0, 0.38)"
        overflow="hidden"
      >
        <ModalBody p={0}>{content}</ModalBody>
      </ModalContent>
    </Modal>
  )
}
